import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart, resetCartId } from "@/context/CartContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div
      data-testid="checkout-success-page"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-2xl"
    >
      <div className="overline mb-4">Checkout</div>
      {status === "checking" && (
        <>
          <h1 className="font-serif-display text-5xl text-bone mb-4">
            Sealing the envelope…
          </h1>
          <p className="text-muted-bone">
            Confirming your payment. Please do not refresh.
          </p>
        </>
      )}
      {status === "paid" && (
        <>
          <h1 className="font-serif-display text-5xl text-bone mb-4">
            Yours is on its way.
          </h1>
          <p className="text-muted-bone mb-2">
            Payment confirmed —{" "}
            <span className="text-bone">
              ${((info?.amount_total || 0) / 100).toFixed(2)}{" "}
              {(info?.currency || "usd").toUpperCase()}
            </span>
          </p>
          <p className="text-muted-bone mb-10 max-w-lg">
            We'll send a discreet confirmation to your inbox. Plain packaging
            ships within 48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              data-testid="success-continue-btn"
              to="/shop"
              className="px-8 py-4 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition text-center"
              style={{ background: "var(--rs-oxblood)" }}
            >
              Continue shopping
            </Link>
            <Link
              data-testid="success-account-btn"
              to="/account"
              className="px-8 py-4 uppercase text-xs tracking-[0.22em] text-bone border hover:bg-white/5 transition text-center"
              style={{ borderColor: "rgba(245,242,235,0.25)" }}
            >
              View order
            </Link>
          </div>
        </>
      )}
      {status === "expired" && (
        <>
          <h1 className="font-serif-display text-5xl text-bone mb-4">
            That session expired.
          </h1>
          <p className="text-muted-bone mb-8">
            No charge was made. You can try again from your cart.
          </p>
          <Link
            to="/shop"
            className="px-8 py-4 uppercase text-xs tracking-[0.22em] text-bone inline-block"
            style={{ background: "var(--rs-oxblood)" }}
          >
            Back to shop
          </Link>
        </>
      )}
      {status === "timeout" && (
        <>
          <h1 className="font-serif-display text-5xl text-bone mb-4">
            Still processing.
          </h1>
          <p className="text-muted-bone">
            Your payment is taking longer than usual. Check your email — we'll
            confirm shortly.
          </p>
        </>
      )}
      {status === "error" && (
        <>
          <h1 className="font-serif-display text-5xl text-bone mb-4">
            Something went sideways.
          </h1>
          <p className="text-muted-bone">
            We couldn't verify the session. If you were charged, contact us and
            we'll sort it.
          </p>
        </>
      )}
    </div>
  );
};

export default CheckoutSuccess;
